import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {ProcessFlowchartConfigWithParentAttributes} from 'config-migration/MigrationConstants';
import {PrimaryObjectPlan} from 'config-migration/types/MigrationObjects';
import {ObjectAction, ObjectActionType} from 'config-migration/types/objectActions';
import {PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT, PrimaryObjectType} from 'config-migration/types/ObjectType';
import getTestProps from 'test/lib/cypress/getTestProps';

import ObjectActionDropdown from '../ObjectActionDropdown';
import {computeFilteredActions} from '../planning-logic/ConfigMigrationPlan';
import TemplatePlanRow from '../templates-step/TemplatePlanRow';
import {ImportPlanSectionProps, ImportPlanSummary} from './ImportPlanSection';
import ImportPlanSectionRow from './ImportPlanSectionRow';

export const PROCEDURE_CHILD_TYPES: ReadonlySet<PrimaryObjectType> = new Set([
  PrimaryObjectType.ProcessFlowchartConfigVersion,
  PrimaryObjectType.ProcessTaskSchema,
]);

function isProcedureLinkedChild(plan: PrimaryObjectPlan): boolean {
  return (
    PROCEDURE_CHILD_TYPES.has(plan.objectType) && plan.objectAttributes.hasOwnProperty('parentIdentifier')
  );
}

/**
 * Derive a Procedure child object's action from the Procedure's action.
 *
 * FCCV versions are immutable, so on Procedure UPDATE a new FCCV is always created.
 * Task schemas are mutable, so on Procedure UPDATE the task schema uses its own initial action
 * (which already reflects provenance matching on the destination).
 */
export function deriveProcedureChildAction(
  procedurePlan: PrimaryObjectPlan,
  childPlan: PrimaryObjectPlan
): PrimaryObjectPlan {
  let childAction: ObjectAction;
  if (
    procedurePlan.selectedAction.type === ObjectActionType.CREATE ||
    procedurePlan.selectedAction.type === ObjectActionType.IGNORE
  ) {
    childAction = {type: procedurePlan.selectedAction.type};
  } else if (childPlan.objectType === PrimaryObjectType.ProcessFlowchartConfigVersion) {
    childAction = {type: ObjectActionType.CREATE};
  } else {
    childAction = childPlan.initialAction;
  }
  return {
    ...childPlan,
    selectedAction: childAction,
    childMigrationObjectsByType: new Map(),
  };
}

export default function GrouperPlanSection(props: ImportPlanSectionProps): JSX.Element {
  const grouperPlan = props.plans[0];
  const groupedPlans = props.plans.slice(1);

  const isTemplateSection = grouperPlan.objectType === PrimaryObjectType.TemplateCollection;

  // For template sections, identify Procedure-linked children and group them by parent Procedure
  const childrenByProcedureId: {[procedureId: string]: Array<PrimaryObjectPlan>} = {};
  const procedureChildObjectIds = new Set<string>();
  if (isTemplateSection) {
    for (const plan of groupedPlans) {
      if (isProcedureLinkedChild(plan)) {
        const parentId = (plan.objectAttributes as ProcessFlowchartConfigWithParentAttributes)
          .parentIdentifier;
        if (!childrenByProcedureId[parentId]) {
          childrenByProcedureId[parentId] = [];
        }
        childrenByProcedureId[parentId].push(plan);
        procedureChildObjectIds.add(plan.objectId);
      }
    }
  }

  const deriveChildActionsForProcedure = (procedurePlan: PrimaryObjectPlan): Array<PrimaryObjectPlan> => {
    const children = childrenByProcedureId[procedurePlan.objectId] || [];
    return children.map((child) => deriveProcedureChildAction(procedurePlan, child));
  };

  const onUpdateGrouperPlan = (updatedGrouperPlan: PrimaryObjectPlan): void => {
    if (updatedGrouperPlan.selectedAction.type === ObjectActionType.IGNORE) {
      const updatedGroupedPlans: Array<PrimaryObjectPlan> = groupedPlans.map((plan) => ({
        ...plan,
        selectedAction: {type: ObjectActionType.IGNORE},
      }));
      props.onUpdatePlans([updatedGrouperPlan, ...updatedGroupedPlans]);
    } else {
      const updatedGroupedPlans = groupedPlans.map((plan) => {
        // For Procedure children, derive action from their parent Procedure's initial action
        if (procedureChildObjectIds.has(plan.objectId)) {
          const parentId = (plan.objectAttributes as ProcessFlowchartConfigWithParentAttributes)
            .parentIdentifier;
          const parentProcedure = groupedPlans.find((p) => p.objectId === parentId);
          if (parentProcedure) {
            return deriveProcedureChildAction(
              {...parentProcedure, selectedAction: parentProcedure.initialAction},
              plan
            );
          }
        }
        return {...plan, selectedAction: plan.initialAction};
      });
      props.onUpdatePlans([updatedGrouperPlan, ...updatedGroupedPlans]);
      props.onUpdateCollapsed(false);
    }
  };

  const {showIgnore, filteredAdditionalActions, showLockIcon} = computeFilteredActions(grouperPlan);

  // Plans to render — excluding Procedure children (their actions are derived, not shown)
  const visibleGroupedPlans = groupedPlans.filter((plan) => !procedureChildObjectIds.has(plan.objectId));

  return (
    <div className="simpleDivTable u-spacing-top">
      <div className={classNames('simpleDivTable-header', 'u-lr-split', css(styles.summaryRow))}>
        <h4>{PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT[grouperPlan.objectType]}</h4>
        <ImportPlanSummary
          plans={props.plans}
          onUpdateCollapsed={props.onUpdateCollapsed}
          collapsed={props.collapsed}
        />
      </div>
      <div
        className="simpleDivTable-header"
        {...getTestProps({component: 'GrouperSection', element: 'tableHeader'})}
      >
        <div className={classNames('simpleDivTable-headerCell u-black-color', css(styles.firstCell))}>
          <strong>{grouperPlan.objectDisplayName}</strong>
        </div>
        <div className="simpleDivTable-headerCell">
          <ObjectActionDropdown
            object={grouperPlan}
            onUpdateObject={onUpdateGrouperPlan}
            showIgnore={showIgnore}
            filteredAdditionalActions={filteredAdditionalActions}
            disableDropdown={showLockIcon}
          />
        </div>
      </div>
      {!props.collapsed && (
        <>
          {visibleGroupedPlans.map((plan) => {
            if (isTemplateSection) {
              const procedureChildren = childrenByProcedureId[plan.objectId] || [];
              const onUpdatePlan = (updatedPlan: PrimaryObjectPlan): void => {
                if (procedureChildren.length > 0) {
                  const derivedChildPlans = deriveChildActionsForProcedure(updatedPlan);
                  props.onUpdatePlans([updatedPlan, ...derivedChildPlans]);
                } else {
                  props.onUpdatePlans([updatedPlan]);
                }
              };
              return (
                <TemplatePlanRow
                  key={plan.objectId}
                  plan={plan}
                  errors={props.errorsByObjectId[plan.objectId] || []}
                  onUpdatePlan={onUpdatePlan}
                  templateCollectionPlan={grouperPlan}
                />
              );
            } else {
              return (
                <ImportPlanSectionRow
                  key={plan.objectId}
                  plan={plan}
                  errors={props.errorsByObjectId[plan.objectId] || []}
                  onUpdatePlan={(updatedPlan) => {
                    props.onUpdatePlans([updatedPlan]);
                  }}
                  highImpactChangeMap={props.highImpactChangeMap}
                  nestedDepth={0}
                />
              );
            }
          })}
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  summaryRow: {
    paddingLeft: 24,
    paddingRight: 24,
    marginBottom: 2,
  },
  firstCell: {
    marginLeft: 12,
  },
});
